import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FaRegCalendarAlt } from 'react-icons/fa';
import DateCalendar from './DateCalendar';
import useDateField from '../../../Hooks/useDateField';
import { useTempEdgeTranslator } from '../../../Hooks/useTranslation';
import { resolveDateLimits } from '../../../utils/calendar';
import { DATE_FORMAT } from '../Constants/Constants';
import '../../../assets/styles/components/DateField.css';

const DateField = ({
  selectedValue,
  onChange,
  onBlur,
  customClass = 'form-control tempEdge-input-box',
  name,
  placeholder,
  placeholderText,
  minDate,
  initDate,
  maxDate,
  yearLimit,
  disabled = false,
  clearable = false,
  testId = 'calendar',
  inputTestId,
  alwaysOpen = false
}) => {
  const { tempedgeTranslator, locale } = useTempEdgeTranslator();
  const limits = useMemo(() => resolveDateLimits({ minDate, initDate, maxDate, yearLimit }), [minDate, initDate, maxDate, yearLimit]);

  const field = useDateField({ value: selectedValue, onChange, onBlur, minDate: limits.min, maxDate: limits.max, alwaysOpen });

  const calendar = (
    <DateCalendar
      selected={field.selected}
      viewDate={field.viewDate}
      minDate={limits.min}
      maxDate={limits.max}
      locale={locale}
      onSelect={field.handleSelect}
      onViewChange={field.setViewDate}
      onClear={clearable ? field.handleClear : undefined}
      testId={`${testId}-panel`}
    />
  );
  const panel = alwaysOpen ? (
    <div className="te-date-field-inline">{calendar}</div>
  ) : (
    createPortal(
      <div className="te-date-field-floating" ref={field.panelRef}>
        {calendar}
      </div>,
      document.body
    )
  );

  return (
    <div className={`te-date-field ${field.open ? 'is-open' : ''}`} ref={field.containerRef}>
      <input
        type="text"
        className={`${customClass} te-date-field-input`}
        name={name}
        value={field.text}
        placeholder={placeholder || placeholderText || DATE_FORMAT}
        disabled={disabled}
        autoComplete="off"
        data-testid={inputTestId || `${testId}-input`}
        onChange={field.handleTextChange}
        onBlur={field.handleBlur}
        onFocus={() => field.setOpen(true)}
      />
      <button
        type="button"
        className="te-date-field-toggle"
        disabled={disabled}
        aria-label={tempedgeTranslator('com.tempedge.msg.label.date')}
        data-testid={`${testId}-toggle`}
        tabIndex={-1}
        onClick={() => field.setOpen(!field.open)}
      >
        <FaRegCalendarAlt aria-hidden="true" />
      </button>
      {field.open && !disabled && panel}
    </div>
  );
};

export default DateField;
